import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import isEmailValid from 'validator/lib/isEmail';

import { Table } from '@hero/molecules';

const Wrapper = styled.div``;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const SpreadSheetRecipientTable = ({
  recipients,
  updateRecipients,
  onDeleteRecipient,
  components,
  actionProps = {
    isEditable: true
  }
}) => {
  const { t } = useTranslation();

  const [globalFilters, setGlobalFilters] = useState({});
  const [editRow, setEditRow] = useState(null);

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const pageCount = useMemo(() => {
    return Math.ceil(recipients.length / pageSize);
  }, [recipients.length, pageSize]);

  const contactsByPage = useMemo(() => {
    let pageIndex = 0;
    const result = {};

    while (pageIndex < pageCount) {
      result[pageIndex] = recipients?.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
      pageIndex += 1;
    }

    return result;
  }, [recipients]);

  const columns = React.useMemo(
    () => [
      {
        id: 'email',
        header: t('email'),
        size: 120,
        accessorKey: 'email',
        isValidFn: val => val && isEmailValid(val),
        editRow,
        editable: true,
        componentProps: {
          maxLength: 100
        }
      },
      {
        header: t('nameOptional'),
        size: 100,
        accessorKey: 'firstName',
        editRow,
        editable: true,
        componentProps: {
          maxLength: 30
        },
        ...(!actionProps?.isEditable && {
          cell: ({ row }) => {
            return `${row.original.firstName || ''} ${row.original.lastName || ''}`;
          }
        })
      },
      {
        id: 'actions',
        header: '',
        size: 5,
        cell: ({ table, row }) => {
          const { updateRecipients } = table.getState();
          const { editMode } = table.options;
          const { editing } = table.getState();
          const rowIsEditing = editing[row.original.id];

          if (editMode === 'row' && rowIsEditing) {
            return (
              <ActionButtons>
                <IconButton
                  variant="text"
                  color="success"
                  onClick={() => {
                    setEditRow(null);
                    table.options.meta.handleSave(row);
                  }}
                >
                  <Icon
                    color="var(--color-components-button-secondary-text-fg)"
                    name="success"
                    size="lg"
                  />
                </IconButton>
                <IconButton
                  variant="text"
                  color="error"
                  onClick={() => {
                    setEditRow(null);
                    table.options.meta.handleCancel(row);
                  }}
                >
                  <Icon
                    color="var(--color-components-button-secondary-text-fg)"
                    name="close"
                    size="lg"
                  />
                </IconButton>
              </ActionButtons>
            );
          }

          return (
            <ActionButtons>
              {actionProps?.isEditable && (
                <IconButton
                  variant="text"
                  color="fg"
                  onClick={() => {
                    setEditRow(prev => (prev !== row.id ? row.id : null));
                    table.options.meta.handleEdit(row);
                  }}
                >
                  <Icon name="edit" size="md" />
                </IconButton>
              )}
              <IconButton
                variant="text"
                color="error"
                onClick={() => {
                  if (onDeleteRecipient) return onDeleteRecipient(row.original);
                  updateRecipients(recipients => {
                    return recipients.filter(recipient => recipient.id !== row.id);
                  });
                }}
              >
                <Icon name="delete" size="md" />
              </IconButton>
            </ActionButtons>
          );
        }
      }
    ],
    [editRow]
  );

  const handleRowUpdate = useCallback(
    updatedRow => {
      updateRecipients(contacts => {
        return contacts.map(contact => {
          if (contact.id === updatedRow.id) {
            return {
              ...contact,
              ...updatedRow,
              hasError: !isEmailValid(updatedRow.email)
            };
          }
          return contact;
        });
      });
    },
    [updateRecipients]
  );

  return (
    <Wrapper>
      <Table
        columns={columns}
        data={contactsByPage[pageIndex] || []}
        state={{
          updateRecipients,
          globalFilter: globalFilters
        }}
        updateData={handleRowUpdate}
        setPagination={setPagination}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        useGlobalFuzzyFilter
        manualPagination
        pagination={{
          pageIndex,
          pageSize,
          total: recipients?.length
        }}
        pageCount={pageCount}
        enabled={{ filter: false, header: false }}
        sectionHeaderProps={{
          enabled: recipients?.length,
          searchProps: {
            placeholder: t('search')
          }
        }}
        editMode="row"
        components={components}
      />
    </Wrapper>
  );
};

SpreadSheetRecipientTable.propTypes = {
  recipients: PropTypes.array,
  pagination: PropTypes.shape({ total: PropTypes.number }),
  updateRecipients: PropTypes.func,
  onDeleteRecipient: PropTypes.func,
  isLoading: PropTypes.bool,
  components: PropTypes.object,
  actionProps: PropTypes.shape({ isEditable: PropTypes.bool })
};

export { SpreadSheetRecipientTable };
